import { createApi } from '@reduxjs/toolkit/query/react';

import { AllDataResponseDto, DeleteParamsDto, DeleteStatusDto, PaginatedResponse, UserMin } from '../common/dto/common.dto';
import { FilterDto, ScheduleStatusFilterDto } from '../common/dto/filter.dto';
import { ScheduleCreateDto, ScheduleUpdateDto, ScheduleViewDto } from '../common/dto/schedule.dto';
import { ScheduleStatusViewDto } from '../common/dto/schedule-status.dto';
import customFetchBase from './base.service';

const API_URI = import.meta.env.VITE_DATA_COLLECTION_SERVICE;
const TAG_TYPE = 'SchedulesRules';

export const schedulesRulesApi = createApi({
  reducerPath: 'schedulesRulesApi',
  baseQuery: customFetchBase(`${API_URI}/api/schedule`),
  tagTypes: [TAG_TYPE],
  endpoints: (builder) => ({
    getSchedulesList: builder.mutation<PaginatedResponse<ScheduleViewDto>, Partial<FilterDto>>({
      query(body) {
        return {
          url: ``,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    getAssignedUserForSchedule: builder.mutation<AllDataResponseDto<UserMin>, Partial<string>>({
      query(locationId) {
        return {
          url: 'get-assigned-users',
          method: 'PATCH',
          body: { locationId },
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    getReminderUserForSchedule: builder.mutation<AllDataResponseDto<UserMin>, Partial<string>>({
      query(locationId) {
        return {
          url: 'get-reminder-users',
          method: 'PATCH',
          body: { locationId },
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    getScheduleStatus: builder.mutation<AllDataResponseDto<ScheduleStatusViewDto>, Partial<ScheduleStatusFilterDto>>({
      query(body) {
        return {
          url: `schedule-status`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    getSchedule: builder.query<ScheduleViewDto, string>({
      query: (id: string) => {
        return `/${id}`;
      },
      providesTags: [TAG_TYPE],
    }),
    addSchedule: builder.mutation<ScheduleViewDto, Partial<ScheduleCreateDto>>({
      query(body) {
        return {
          url: ``,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    updateSchedule: builder.mutation<ScheduleViewDto, Partial<ScheduleUpdateDto>>({
      query(data) {
        return {
          url: ``,
          method: 'PUT',
          body: data,
        };
      },
    }),
    deleteSchedule: builder.mutation<DeleteStatusDto, DeleteParamsDto>({
      query(data: DeleteParamsDto) {
        return {
          url: data.id,
          method: 'DELETE',
          body: data.params ?? {},
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
    deleteScheduleStatus: builder.mutation<DeleteStatusDto, DeleteParamsDto>({
      query(data: DeleteParamsDto) {
        return {
          url: `schedule-status/${data.id}`,
          method: 'DELETE',
          body: data.params ?? {},
        };
      },
      invalidatesTags: [TAG_TYPE],
    }),
  }),
});

// auto-generated based on the defined endpoints
export const {
  useGetSchedulesListMutation,
  useGetAssignedUserForScheduleMutation,
  useGetReminderUserForScheduleMutation,
  useGetScheduleStatusMutation,
  useAddScheduleMutation,
  useUpdateScheduleMutation,
  useGetScheduleQuery,
  useDeleteScheduleMutation,
  useDeleteScheduleStatusMutation,
} = schedulesRulesApi;
